import { useState, useEffect } from 'react'
import {
    Plus,
    Edit3,
    Trash2,
    CreditCard,
    CheckCircle,
    XCircle,
    Search,
    DollarSign,
    Users,
    Building2,
    BarChart3,
    AlertCircle
} from 'lucide-react'
import { supabase } from '../services/supabase'
import { useToast } from '../hooks/useToast'
import Button from '../components/Button'
import { ToastContainer } from '../components/Toast'
import {
    PERIODICIDADES_PIX,
    PERIODICIDADE_LABELS,
    PERIODICIDADE_SUFIXO_PRECO,
    normalizarPeriodicidade,
    type PeriodicidadePix,
} from '../utils/periodicidadePix'

interface Plano {
    id: string
    nome: string
    nome_display: string
    descricao: string | null
    preco_mensal: number
    preco_anual: number | null
    periodicidade: PeriodicidadePix | string
    periodo_trial: number
    recursos: string[] | null
    limite_vendas: number | null
    limite_corretores: number | null
    limite_imoveis: number | null
    suporte_prioritario: boolean
    relatorios_avancados: boolean
    api_access: boolean
    status: 'ativo' | 'inativo' | 'arquivado'
    ordem_exibicao: number
    data_criacao: string
    data_atualizacao: string
}

interface PlanoFormData {
    nome: string
    nome_display: string
    descricao: string
    preco_mensal: string
    preco_anual: string
    periodicidade: PeriodicidadePix
    periodo_trial: number
    recursos: string[]
    limite_vendas: string
    limite_corretores: string
    limite_imoveis: string
    suporte_prioritario: boolean
    relatorios_avancados: boolean
    api_access: boolean
    status: 'ativo' | 'inativo' | 'arquivado'
    ordem_exibicao: number
}

export default function AdminPlanos() {
    const { toasts, showSuccess, showError } = useToast()
    const [planos, setPlanos] = useState<Plano[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('todos')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingPlano, setEditingPlano] = useState<Plano | null>(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [planoToDelete, setPlanoToDelete] = useState<Plano | null>(null)
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

    const [formData, setFormData] = useState<PlanoFormData>({
        nome: '',
        nome_display: '',
        descricao: '',
        preco_mensal: '',
        preco_anual: '',
        periodicidade: 'MENSAL',
        periodo_trial: 7,
        recursos: [],
        limite_vendas: '',
        limite_corretores: '',
        limite_imoveis: '',
        suporte_prioritario: false,
        relatorios_avancados: false,
        api_access: false,
        status: 'ativo',
        ordem_exibicao: 1
    })

    const [recursoInput, setRecursoInput] = useState('')

    useEffect(() => {
        loadPlanos()
    }, [])

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            const dropdown = target.closest('.dropdown-container')

            if (activeDropdown && !dropdown) {
                setActiveDropdown(null)
            }
        }

        if (activeDropdown) {
            document.addEventListener('click', handleClickOutside)
        }

        return () => {
            document.removeEventListener('click', handleClickOutside)
        }
    }, [activeDropdown])

    const loadPlanos = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('planos')
                .select('*')
                .order('ordem_exibicao', { ascending: true })

            if (error) throw error

            setPlanos(data || [])
        } catch (err) {
            console.error('Erro ao carregar planos:', err)
            showError('Erro ao carregar planos')
        } finally {
            setLoading(false)
        }
    }

    const openModal = (plano?: Plano) => {
        if (plano) {
            setEditingPlano(plano)
            setFormData({
                nome: plano.nome,
                nome_display: plano.nome_display,
                descricao: plano.descricao || '',
                preco_mensal: plano.preco_mensal.toString(),
                preco_anual: plano.preco_anual?.toString() || '',
                periodicidade: normalizarPeriodicidade(plano.periodicidade),
                periodo_trial: plano.periodo_trial,
                recursos: Array.isArray(plano.recursos) ? plano.recursos : [],
                limite_vendas: plano.limite_vendas?.toString() || '',
                limite_corretores: plano.limite_corretores?.toString() || '',
                limite_imoveis: plano.limite_imoveis?.toString() || '',
                suporte_prioritario: plano.suporte_prioritario,
                relatorios_avancados: plano.relatorios_avancados,
                api_access: plano.api_access,
                status: plano.status,
                ordem_exibicao: plano.ordem_exibicao
            })
        } else {
            setEditingPlano(null)
            setFormData({
                nome: '',
                nome_display: '',
                descricao: '',
                preco_mensal: '',
                preco_anual: '',
                periodicidade: 'MENSAL',
                periodo_trial: 7,
                recursos: [],
                limite_vendas: '',
                limite_corretores: '',
                limite_imoveis: '',
                suporte_prioritario: false,
                relatorios_avancados: false,
                api_access: false,
                status: 'ativo',
                ordem_exibicao: planos.length + 1
            })
        }
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setEditingPlano(null)
        setRecursoInput('')
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        try {
            const planoData = {
                nome: formData.nome,
                nome_display: formData.nome_display,
                descricao: formData.descricao || null,
                preco_mensal: parseFloat(formData.preco_mensal),
                preco_anual: formData.preco_anual ? parseFloat(formData.preco_anual) : null,
                periodicidade: formData.periodicidade,
                periodo_trial: formData.periodo_trial,
                recursos: formData.recursos.length > 0 ? formData.recursos : null,
                limite_vendas: formData.limite_vendas ? parseInt(formData.limite_vendas) : null,
                limite_corretores: formData.limite_corretores ? parseInt(formData.limite_corretores) : null,
                limite_imoveis: formData.limite_imoveis ? parseInt(formData.limite_imoveis) : null,
                suporte_prioritario: formData.suporte_prioritario,
                relatorios_avancados: formData.relatorios_avancados,
                api_access: formData.api_access,
                status: formData.status,
                ordem_exibicao: formData.ordem_exibicao,
                data_atualizacao: new Date().toISOString()
            }

            if (editingPlano) {
                const { error } = await supabase
                    .from('planos')
                    .update(planoData)
                    .eq('id', editingPlano.id)

                if (error) throw error
                showSuccess('Plano atualizado com sucesso!')
            } else {
                const { error } = await supabase
                    .from('planos')
                    .insert([planoData])

                if (error) throw error
                showSuccess('Plano criado com sucesso!')
            }

            closeModal()
            loadPlanos()
        } catch (err: any) {
            console.error('Erro ao salvar plano:', err)
            showError(err.message || 'Erro ao salvar plano')
        }
    }

    const handleDelete = async () => {
        if (!planoToDelete) return

        try {
            const { error } = await supabase
                .from('planos')
                .delete()
                .eq('id', planoToDelete.id)

            if (error) throw error

            showSuccess('Plano excluído com sucesso!')
            setIsDeleteModalOpen(false)
            setPlanoToDelete(null)
            loadPlanos()
        } catch (err: any) {
            console.error('Erro ao excluir plano:', err)
            showError(err.message || 'Erro ao excluir plano')
        }
    }

    const addRecurso = () => {
        if (recursoInput.trim()) {
            setFormData({
                ...formData,
                recursos: [...formData.recursos, recursoInput.trim()]
            })
            setRecursoInput('')
        }
    }

    const removeRecurso = (index: number) => {
        setFormData({
            ...formData,
            recursos: formData.recursos.filter((_, i) => i !== index)
        })
    }

    const filteredPlanos = planos.filter(plano => {
        const matchSearch = !searchTerm ||
            plano.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            plano.nome_display.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (plano.descricao && plano.descricao.toLowerCase().includes(searchTerm.toLowerCase()))

        const matchStatus = statusFilter === 'todos' || plano.status === statusFilter

        return matchSearch && matchStatus
    })

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    if (loading) {
        return (
            <div className="w-full">
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 dark:text-gray-400">Carregando planos...</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full">
            <div className="w-full space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                            Gerenciar Planos
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Gerencie os planos de assinatura disponíveis
                        </p>
                    </div>
                    <Button
                        onClick={() => openModal()}
                        variant="btn_add"
                        className="flex items-center gap-2"
                    >
                        <Plus size={20} />
                        Novo Plano
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Total de Planos
                                </p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                                    {planos.length}
                                </p>
                            </div>
                            <CreditCard className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Planos Ativos
                                </p>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                                    {planos.filter(p => p.status === 'ativo').length}
                                </p>
                            </div>
                            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Planos Inativos
                                </p>
                                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                                    {planos.filter(p => p.status === 'inativo').length}
                                </p>
                            </div>
                            <XCircle className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    Receita Mensal Potencial
                                </p>
                                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                                    {formatCurrency(planos.filter(p => p.status === 'ativo').reduce((sum, p) => sum + p.preco_mensal, 0))}
                                </p>
                            </div>
                            <DollarSign className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                        </div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Busca */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                            <input
                                type="text"
                                placeholder="Buscar planos por nome ou descrição..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        {/* Filtro Status */}
                        <div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="todos">Todos os Status</option>
                                <option value="ativo">Ativos</option>
                                <option value="inativo">Inativos</option>
                                <option value="arquivado">Arquivados</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Lista de Planos */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Lista de Planos
                        </h2>

                        {filteredPlanos.length === 0 ? (
                            <div className="text-center py-8">
                                <CreditCard className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400">Nenhum plano encontrado</p>
                                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                    Clique em "Novo Plano" para criar o primeiro plano
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-gray-700">
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Nome</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Preço / Ciclo</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Periodicidade</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Trial</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Limites</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Recursos</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Status</th>
                                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Ordem</th>
                                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPlanos.map((plano) => (
                                            <tr key={plano.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="py-3 px-4">
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-gray-100">{plano.nome_display}</p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">{plano.nome}</p>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-gray-100">
                                                            {formatCurrency(plano.preco_mensal)}
                                                            {PERIODICIDADE_SUFIXO_PRECO[normalizarPeriodicidade(plano.periodicidade)]}
                                                        </p>
                                                        {plano.preco_anual && (
                                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                {formatCurrency(plano.preco_anual)}/ano
                                                            </p>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="text-sm text-gray-900 dark:text-gray-100">
                                                        {PERIODICIDADE_LABELS[normalizarPeriodicidade(plano.periodicidade)]}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="text-sm text-gray-900 dark:text-gray-100">
                                                        {plano.periodo_trial} dias
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                                        {plano.limite_vendas !== null ? (
                                                            <p><BarChart3 className="inline w-3 h-3 mr-1" />{plano.limite_vendas} vendas/mês</p>
                                                        ) : (
                                                            <p><BarChart3 className="inline w-3 h-3 mr-1" />Ilimitado</p>
                                                        )}
                                                        {plano.limite_corretores !== null ? (
                                                            <p><Users className="inline w-3 h-3 mr-1" />{plano.limite_corretores} corretores</p>
                                                        ) : (
                                                            <p><Users className="inline w-3 h-3 mr-1" />Ilimitado</p>
                                                        )}
                                                        {plano.limite_imoveis !== null ? (
                                                            <p><Building2 className="inline w-3 h-3 mr-1" />{plano.limite_imoveis} imóveis</p>
                                                        ) : (
                                                            <p><Building2 className="inline w-3 h-3 mr-1" />Ilimitado</p>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex flex-wrap gap-1">
                                                        {plano.suporte_prioritario && (
                                                            <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">Suporte</span>
                                                        )}
                                                        {plano.relatorios_avancados && (
                                                            <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded">Relatórios</span>
                                                        )}
                                                        {plano.api_access && (
                                                            <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded">API</span>
                                                        )}
                                                        {Array.isArray(plano.recursos) && plano.recursos.length > 0 && (
                                                            <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded">
                                                                +{plano.recursos.length}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-1 text-xs rounded ${
                                                        plano.status === 'ativo' 
                                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                                            : plano.status === 'inativo'
                                                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                                    }`}>
                                                        {plano.status === 'ativo' ? 'Ativo' : plano.status === 'inativo' ? 'Inativo' : 'Arquivado'}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="text-sm text-gray-900 dark:text-gray-100">
                                                        {plano.ordem_exibicao}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => openModal(plano)}
                                                            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                            title="Editar"
                                                        >
                                                            <Edit3 size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setPlanoToDelete(plano)
                                                                setIsDeleteModalOpen(true)
                                                            }}
                                                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                            title="Excluir"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Criar/Editar Plano */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                        {editingPlano ? 'Editar Plano' : 'Novo Plano'}
                                    </h2>
                                    <button
                                        onClick={closeModal}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                        <XCircle size={24} />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Nome */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Nome (código) *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.nome}
                                                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="start, medium, pro"
                                                required
                                            />
                                        </div>

                                        {/* Nome Display */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Nome de Exibição *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.nome_display}
                                                onChange={(e) => setFormData({ ...formData, nome_display: e.target.value })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="Start, Medium, Pro"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Descrição */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Descrição
                                        </label>
                                        <textarea
                                            value={formData.descricao}
                                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                                            className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                            rows={3}
                                            placeholder="Descrição do plano..."
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Periodicidade Pix Automático *
                                            </label>
                                            <select
                                                value={formData.periodicidade}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        periodicidade: e.target.value as PeriodicidadePix,
                                                    })
                                                }
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                required
                                            >
                                                {PERIODICIDADES_PIX.map((periodo) => (
                                                    <option key={periodo} value={periodo}>
                                                        {PERIODICIDADE_LABELS[periodo]}
                                                    </option>
                                                ))}
                                            </select>
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Define o ciclo da cobrança automática (ex.: Semanal = 7 dias).
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Valor do ciclo */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Valor da cobrança (R$) *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.preco_mensal}
                                                onChange={(e) => setFormData({ ...formData, preco_mensal: e.target.value })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="0.00"
                                                required
                                            />
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Preço cobrado a cada ciclo ({PERIODICIDADE_LABELS[formData.periodicidade].toLowerCase()}).
                                            </p>
                                        </div>

                                        {/* Preço Anual (opcional / legado) */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Preço Anual (R$)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.preco_anual}
                                                onChange={(e) => setFormData({ ...formData, preco_anual: e.target.value })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="0.00"
                                            />
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Usado se a periodicidade for Anual (senão, use o valor da cobrança).
                                            </p>
                                        </div>

                                        {/* Período Trial */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Período Trial (dias) *
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.periodo_trial}
                                                onChange={(e) => setFormData({ ...formData, periodo_trial: parseInt(e.target.value) || 0 })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Limite Vendas */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Limite de Vendas/mês (deixe vazio para ilimitado)
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.limite_vendas}
                                                onChange={(e) => setFormData({ ...formData, limite_vendas: e.target.value })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="Ilimitado"
                                            />
                                        </div>

                                        {/* Limite Corretores */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Limite de Corretores (deixe vazio para ilimitado)
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.limite_corretores}
                                                onChange={(e) => setFormData({ ...formData, limite_corretores: e.target.value })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="Ilimitado"
                                            />
                                        </div>

                                        {/* Limite Imóveis */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Limite de Imóveis (deixe vazio para ilimitado)
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.limite_imoveis}
                                                onChange={(e) => setFormData({ ...formData, limite_imoveis: e.target.value })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="Ilimitado"
                                            />
                                        </div>
                                    </div>

                                    {/* Recursos */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Recursos
                                        </label>
                                        <div className="flex gap-2 mb-2">
                                            <input
                                                type="text"
                                                value={recursoInput}
                                                onChange={(e) => setRecursoInput(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault()
                                                        addRecurso()
                                                    }
                                                }}
                                                className="flex-1 p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="Adicionar recurso..."
                                            />
                                            <Button
                                                type="button"
                                                onClick={addRecurso}
                                                variant="btn_secondary"
                                            >
                                                Adicionar
                                            </Button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {formData.recursos.map((recurso, index) => (
                                                <span
                                                    key={index}
                                                    className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-lg flex items-center gap-2"
                                                >
                                                    {recurso}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeRecurso(index)}
                                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                                                    >
                                                        <XCircle size={16} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Recursos Especiais */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Recursos Especiais
                                        </label>
                                        <div className="space-y-2">
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.suporte_prioritario}
                                                    onChange={(e) => setFormData({ ...formData, suporte_prioritario: e.target.checked })}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">Suporte Prioritário</span>
                                            </label>
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.relatorios_avancados}
                                                    onChange={(e) => setFormData({ ...formData, relatorios_avancados: e.target.checked })}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">Relatórios Avançados</span>
                                            </label>
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.api_access}
                                                    onChange={(e) => setFormData({ ...formData, api_access: e.target.checked })}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">Acesso à API</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Status */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Status *
                                            </label>
                                            <select
                                                value={formData.status}
                                                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                required
                                            >
                                                <option value="ativo">Ativo</option>
                                                <option value="inativo">Inativo</option>
                                                <option value="arquivado">Arquivado</option>
                                            </select>
                                        </div>

                                        {/* Ordem de Exibição */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Ordem de Exibição *
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.ordem_exibicao}
                                                onChange={(e) => setFormData({ ...formData, ordem_exibicao: parseInt(e.target.value) || 1 })}
                                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <Button
                                            type="button"
                                            variant="btn_secondary"
                                            onClick={closeModal}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="btn_add"
                                        >
                                            {editingPlano ? 'Salvar Alterações' : 'Criar Plano'}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal Confirmar Exclusão */}
                {isDeleteModalOpen && planoToDelete && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 m-4">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        Confirmar Exclusão
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Esta ação não pode ser desfeita
                                    </p>
                                </div>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 mb-6">
                                Tem certeza que deseja excluir o plano <strong>{planoToDelete.nome_display}</strong>?
                            </p>
                            <div className="flex justify-end gap-3">
                                <Button
                                    variant="btn_secondary"
                                    onClick={() => {
                                        setIsDeleteModalOpen(false)
                                        setPlanoToDelete(null)
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="btn_danger"
                                    onClick={handleDelete}
                                >
                                    Excluir
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Toast Container */}
                <ToastContainer toasts={toasts} />
            </div>
        </div>
    )
}

